import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import { ECredentialsType, ICredential } from 'api/useCredentials';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from 'components/ui/tooltip';

import { formatCredentialDate } from '../helpers/developerSettings.helpers';

import { ECredentialValueRef } from './InvalidationCredentialModal';
import { PasswordInput } from './PasswordInput';

interface ICredentialsRowProps {
  label: string;
  description: string;
  credentialType: ECredentialsType;
  credentials: ICredential[];
  isRotating: boolean;
  valueRef: ECredentialValueRef;
  onCopyCredential: (value: string, label: string) => void;
  onRotateCredential: (credentialType: ECredentialsType) => void;
  onRevokeCredential: (
    credential: ICredential,
    credentialType: ECredentialsType,
    label: string,
    valueRef: ECredentialValueRef
  ) => void;
}

export const CredentialsRow: React.FC<ICredentialsRowProps> = ({
  label,
  description,
  credentialType,
  credentials,
  isRotating,
  valueRef,
  onCopyCredential,
  onRotateCredential,
  onRevokeCredential
}) => {
  const isSecondary = credentials.length > 1;
  const primaryCredential = credentials[0];
  const secondaryCredential = credentials[1];

  const renderSecondaryCredentialContent = (): React.ReactNode => {
    if (!secondaryCredential) {
      return (
        <div className="flex items-end pb-[28px]">
          <Button
            variant="secondary"
            onClick={() => onRotateCredential(credentialType)}
            isLoading={isRotating}
            leftIcon={<FontAwesomeIcon icon="fa-regular fa-circle-plus" />}
            className="w-full bg-base-secondary font-normal shadow-sm"
          >
            Generate New
          </Button>
        </div>
      );
    }

    return (
      <div className="min-w-0 flex flex-col gap-2">
        <PasswordInput
          value={secondaryCredential.value}
          iconActions={[
            {
              icon: 'fa-regular fa-clone',
              className: 'rotate-180 scale-x-[-1]',
              onClick: () =>
                onCopyCredential(
                  secondaryCredential.value,
                  `Secondary ${label}`
                )
            },
            {
              icon: 'fa-regular fa-trash-can',
              className: 'hover:text-red-600',
              onClick: () =>
                onRevokeCredential(
                  secondaryCredential,
                  credentialType,
                  label,
                  valueRef
                )
            }
          ]}
        />
        <CredentialMeta credential={secondaryCredential} />
      </div>
    );
  };

  const secondaryCredentialContent = useMemo(renderSecondaryCredentialContent, [
    credentialType,
    isRotating,
    label,
    onCopyCredential,
    onRotateCredential,
    onRevokeCredential,
    secondaryCredential,
    valueRef
  ]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
        <p className="text-sm font-normal text-gray-500">{description}</p>
      </div>
      <div className="grid grid-cols-2 gap-8">
        <div className="min-w-0 flex flex-col gap-2">
          <PasswordInput
            value={primaryCredential.value}
            iconActions={[
              {
                icon: 'fa-regular fa-clone',
                className: 'rotate-180 scale-x-[-1]',
                onClick: () =>
                  onCopyCredential(
                    primaryCredential.value,
                    `${isSecondary ? 'Primary' : ''} ${label}`
                  )
              },
              {
                icon: 'fa-regular fa-trash-can',
                className:
                  credentials.length > 1 ? 'hover:text-red-600' : undefined,
                onClick: () =>
                  onRevokeCredential(
                    primaryCredential,
                    credentialType,
                    label,
                    valueRef
                  ),
                disabled: credentials.length <= 1
              }
            ]}
          />
          <CredentialMeta credential={primaryCredential} />
        </div>
        {secondaryCredentialContent}
      </div>
    </div>
  );
};

const CredentialMeta: React.FC<{ credential: ICredential }> = ({
  credential
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkIsTruncated = useCallback((): void => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    setIsTruncated(element.scrollWidth > element.clientWidth);
  }, []);

  useLayoutEffect(() => {
    checkIsTruncated();

    const element = textRef.current;
    if (!element) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(checkIsTruncated);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkIsTruncated, credential.createdBy, credential.updatedAt]);

  const content = (
    <>
      created by: {credential.createdBy} &middot;{' '}
      {formatCredentialDate(credential.updatedAt)}
    </>
  );

  const text = (
    <p ref={textRef} className="truncate text-sm text-gray-500">
      {content}
    </p>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{text}</TooltipTrigger>
        {isTruncated && (
          <TooltipContent
            side="bottom"
            className="w-[213px] bg-white px-3 py-1.5 shadow-md"
          >
            <p className="text-sm [overflow-wrap:anywhere]">{content}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};
